import React, { useState } from "react";
import { patchParticipationRequest } from "api/bookings";
import { DetailedBooking, DetailedBookingParticipant, deleteBookingParticipant } from "api/admin/bookings";
import { useSnackbar } from "notistack";
import { Box, Paper } from "@material-ui/core";
import { Column } from "material-table";
import { DefaultTable } from "components";
import moment from "moment";
import i18n from "../i18n";
import InviteParticipants from "./InviteParticipants";

// prop types
interface ParticipantsTabProps {
    booking?: DetailedBooking,
    onRefreshBooking: () => void,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// participants tab in detail dialog
const ParticipantsTab = ({ booking, onRefreshBooking }: ParticipantsTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [showInvitePlayers, setShowInvitePlayers] = useState(false);

    // delete row handler (delete participant)
    const onRowDelete = (oldData: DetailedBookingParticipant) => new Promise<void>((resolve) => {
        deleteBookingParticipant({
            bookingParticipantId: oldData.id,
        }).then(onRefreshBooking)
            .catch((err) => displayError(err))
            .finally(resolve);
    });

    // table actions
    const tableActions = [
        {
            hidden: booking?.bookingType === "blocking" || moment(booking?.endDate) < moment(),
            icon: "add",
            isFreeAction: true,
            tooltip: i18n.invitePlayers,
            onClick: () => setShowInvitePlayers(true),
        },
        ({ id, invitationStatus }: DetailedBookingParticipant) => ({
            hidden: invitationStatus !== "requested-to-join",
            icon: "check",
            tooltip: i18n.accept,
            onClick: () => patchParticipationRequest({
                accept: true,
                bookingParticipantId: id,
            }).then(onRefreshBooking).catch(displayError),
        }),
        ({ id, invitationStatus }: DetailedBookingParticipant) => ({
            hidden: invitationStatus !== "requested-to-join",
            icon: "close",
            tooltip: i18n.decline,
            onClick: () => patchParticipationRequest({
                accept: false,
                bookingParticipantId: id,
            }).then(onRefreshBooking).catch(displayError),
        }),
    ];

    // table columns
    const tableColumns: Column<DetailedBookingParticipant>[] = [
        {
            field: "user.firstName",
            title: i18n.firstName,
        },
        {
            field: "user.lastName",
            title: i18n.lastName,
        },
        {
            field: "participantRole",
            render: ({ participantRole, isCreator }) => `${i18n.participantRoles[participantRole]}${isCreator ? ` (${i18n.creator})` : ""}`,
            title: i18n.participantRole,
        },
        {
            field: "status",
            render: ({ checkInStatus, checkInType, invitationStatus }) => (
                <>
                    <Box>{`${i18n.invitation}: ${i18n.invitationStatus[invitationStatus]}`}</Box>
                    <Box>{`${i18n.checkIn}: ${checkInType === "3rd-party" ? i18n.checkInType3rdParty : i18n.checkInStatus[checkInStatus]}`}</Box>
                </>
            ),
            title: "Status",
        },
        {
            field: "payingPriceCents",
            render: ({ payingPriceCents }) => formatter.format(payingPriceCents / 100),
            title: i18n.payment,
        },
    ];

    // render
    return showInvitePlayers ? (
        <InviteParticipants
            booking={booking}
            onCancel={() => setShowInvitePlayers(false)}
            onRefreshBooking={onRefreshBooking}
        />
    ) : (
        <DefaultTable
            actions={tableActions}
            columns={tableColumns}
            components={{
                Container: (props) => <Paper {...props} variant="outlined" />,
            }}
            data={booking?.participants || []}
            // detailPanel={(rowData) => <ParticipantDetails participant={rowData} />}
            editable={{
                isDeleteHidden: () => moment(booking?.startDate) < moment(), // booking has begun
                onRowDelete,
            }}
            title={i18n.manageParticipants}
        />
    );
};

export default ParticipantsTab;
